/*
 * A partner's referral code, from `?ref=CODE` on any entry URL.
 *
 * It has to survive every step of signup: the email check, the Google round
 * trip (which leaves this origin and comes back in the same tab) and the org
 * step, where it is finally sent with CompleteSignup. So it is kept in
 * sessionStorage: per tab, gone when the tab closes, and never added to any
 * other URL. The `ref` parameter is stripped from the address bar once read,
 * so it is not carried into a bookmark or a shared link either.
 *
 * Codes are case-insensitive and are shown to people as XXXX-XXXX, so the
 * form someone typed or pasted is normalised: upper-cased, with spaces and
 * dashes removed. The server normalises too; this keeps what we store and
 * validate stable.
 */

const KEY = 'dodil_signup_referral';
/** Longer than any code iam issues; anything past this is not a code. */
const MAX_LEN = 32;

export interface StoredReferral {
    code: string;
    /** Set once the public endpoint said the code is usable. */
    partnerDisplayName?: string;
}

/** Upper-case, drop whitespace and dashes. Returns undefined for anything that is not plausibly a code. */
export function normaliseReferralCode(raw: string | null | undefined): string | undefined {
    if (typeof raw !== 'string') return undefined;
    const code = raw.toUpperCase().replace(/[\s\-‐-―]/g, '');
    if (!code || code.length > MAX_LEN || !/^[A-Z0-9]+$/.test(code)) return undefined;
    return code;
}

const session = (): Storage | undefined => {
    try { return window.sessionStorage; } catch { return undefined; }
};

/**
 * Call once at start: keep a valid `?ref=` for the end of signup.
 * A new `ref` replaces an older one; a missing or garbage one leaves any
 * stored code alone. Returns the code now stored, if any.
 */
export function rememberReferral(search: string, storage: Storage | undefined = session()): string | undefined {
    const code = normaliseReferralCode(new URLSearchParams(search).get('ref'));
    if (code && storage) {
        const prev = readReferral(storage);
        // Keep an already-validated name when the same code is seen again.
        const next: StoredReferral = prev?.code === code ? prev : { code };
        try { storage.setItem(KEY, JSON.stringify(next)); } catch { /* storage blocked: no referral */ }
    }
    return readReferral(storage)?.code;
}

export function readReferral(storage: Storage | undefined = session()): StoredReferral | undefined {
    if (!storage) return undefined;
    try {
        const v = JSON.parse(storage.getItem(KEY) || 'null');
        const code = normaliseReferralCode(v?.code);
        if (!code) return undefined;
        return typeof v.partnerDisplayName === 'string' && v.partnerDisplayName
            ? { code, partnerDisplayName: v.partnerDisplayName }
            : { code };
    } catch {
        return undefined;
    }
}

/** Record the partner's display name once the code validated. */
export function markReferralValid(code: string, partnerDisplayName: string, storage: Storage | undefined = session()): void {
    if (!storage || readReferral(storage)?.code !== code) return;
    try { storage.setItem(KEY, JSON.stringify({ code, partnerDisplayName })); } catch { /* ignore */ }
}

export function forgetReferral(storage: Storage | undefined = session()): void {
    try { storage?.removeItem(KEY); } catch { /* ignore */ }
}

/** The search string with `ref` removed (other parameters kept, in order). */
export function stripRefParam(search: string): string {
    const params = new URLSearchParams(search);
    if (!params.has('ref')) return search;
    params.delete('ref');
    const s = params.toString();
    return s ? `?${s}` : '';
}

/**
 * What the public ValidateReferralCode call told us.
 *  - valid:   show "Referred by <name>" and send the code.
 *  - invalid: the server answered and the code is not usable; drop it.
 *  - unknown: rate-limited (429), offline, 5xx or an odd body. Keep the code
 *             and say nothing; CompleteSignup decides, and never fails on it.
 */
export type ReferralCheck =
    | { kind: 'valid'; partnerDisplayName: string }
    | { kind: 'invalid' }
    | { kind: 'unknown' };

export function classifyReferralResult(data: unknown): ReferralCheck {
    const d = data as { valid?: unknown; partnerDisplayName?: unknown; error?: unknown } | null | undefined;
    if (!d || typeof d !== 'object' || 'error' in d) return { kind: 'unknown' };
    if (d.valid === true) {
        const name = typeof d.partnerDisplayName === 'string' ? d.partnerDisplayName.trim() : '';
        return { kind: 'valid', partnerDisplayName: name };
    }
    // proto3 JSON leaves `valid: false` out entirely, so an empty object is a real "no".
    return { kind: 'invalid' };
}

export function classifyReferralError(err: unknown): ReferralCheck {
    const status = (err as { response?: { status?: number } } | null)?.response?.status;
    // An unknown code is a 200 with `valid: false`. Only a 400 (a code the
    // server refuses to even look up) is also a "no". A 404 is the gateway not
    // routing the endpoint yet, and 429, 5xx or no response at all say nothing
    // about the code, so it is kept.
    if (status === 400) return { kind: 'invalid' };
    return { kind: 'unknown' };
}
